import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { USER_TYPE, USER_TYPES } from '@/constants/constants';
import { resetExerciseLibrarySession } from '@/utils/reset-exercise-library-session';
import { resetSessionCheckIn } from '@/utils/reset-session-check-in';
import { clearAuthHistoryFloor } from '@/utils/auth-history-floor';

const AUTH_STORAGE_NAME_BY_USER_TYPE = {
  [USER_TYPES.PATIENT]: 'rehab-auth-patient',
  [USER_TYPES.THERAPIST]: 'rehab-auth-therapist',
  [USER_TYPES.DASHBOARD]: 'rehab-auth-dashboard',
};

const AUTH_STORAGE_NAME = AUTH_STORAGE_NAME_BY_USER_TYPE[USER_TYPE] || 'rehab-auth';

export const useAuth = create(
  persist(
    set => ({
      accessToken: '',
      refreshToken: '',
      user: null, // { id, firstName, lastName, permissions: [{ id, action, subject }], ... }

      setAccessToken: accessToken => set({ accessToken }),

      setRefreshToken: refreshToken => set({ refreshToken }),

      setUser: user => set({ user: user ?? null }),

      setTokens: ({ accessToken, refreshToken }) =>
        set({
          accessToken: accessToken || '',
          refreshToken: refreshToken || '',
        }),

      clearAuth: () => {
        try {
          const userTypeStore = localStorage.getItem('rehab-user-type');
          if (userTypeStore) {
            const parsed = JSON.parse(userTypeStore);
            parsed.state.userType = USER_TYPE;
            localStorage.setItem('rehab-user-type', JSON.stringify(parsed));
          }
          localStorage.removeItem('user-info');
          // Keep the plain 'user-type' key in sync with the reset above -
          // otherwise getHomePath()/route guards can keep reading a stale
          // type after auth has been cleared.
          localStorage.removeItem('user-type');
        } catch (error) {
          console.error('Error resetting user type:', error);
        }

        resetExerciseLibrarySession();
        resetSessionCheckIn();
        clearAuthHistoryFloor();

        return set({
          accessToken: '',
          refreshToken: '',
          user: null,
        });
      },

      getUserId: () => {
        const state = useAuth.getState();
        if (state.user?.id !== undefined && state.user?.id !== null) {
          return state.user.id;
        }
        try {
          const staffInfo = JSON.parse(localStorage.getItem('user-info'));
          return staffInfo?.id ?? null;
        } catch (_) {
          return null;
        }
      },
      // Legacy methods (for backward compatibility)
      addUserData: token => set({ accessToken: token }),
      removeUserEmail: () => set({ accessToken: '' }),
      addUserNewEmail: email => set({ email: email.toLowerCase() }),
      removeNewEmail: () => set({ email: '' }),
    }),
    {
      name: AUTH_STORAGE_NAME, // name of the item in the storage (must be unique)
      storage: createJSONStorage(() => localStorage), // (optional) by default, 'localStorage' is used
    }
  )
);

// The in-memory store can go stale relative to localStorage: a page restored from the
// back/forward cache (e.g. pressing Back from the dashboard to the /lobby document the
// magic link was opened on top of) keeps its pre-login state, and another tab can log in
// or out. Re-read the persisted auth in both cases so the route guards see the real session.
if (typeof window !== 'undefined') {
  window.addEventListener('pageshow', event => {
    if (event.persisted) {
      useAuth.persist.rehydrate();
    }
  });
  window.addEventListener('storage', event => {
    if (event.key === AUTH_STORAGE_NAME) {
      useAuth.persist.rehydrate();
    }
  });
}
